import React, { useEffect, useState } from 'react';
import { useGlobalDialogContext } from 'App/components/Layouts/Dashboard/dialog/GlobalDialog';
import { ETwilioCallbackSmsStatus } from 'store/request/interfaces';
import requestService from 'store/request/service';
import { MessageDeliveryErrorDialog } from './index';
import { TwilioError } from '../../../../integrations/twilio/error/types';
import { twilioUndeliveredSmsError } from '../../../../integrations/twilio/error/constants';
import { buildLinkToCandidateAuth } from 'functions/linkUtils';
import { WebSocketClientMessageTypes } from 'contexts/webSocketClient/messageTypes';
import { useWebSocketClientMessageSubscription } from 'contexts/webSocketClient/useWebSocketClientSubscription';
import { useLogsManager } from 'integrations/logs';

type InputProps = {
  request: any;
  lastUsedText: string;
  error?: TwilioError;
};

export const MessageDeliveryErrorGlobalDialog = ({
  request,
  lastUsedText,
  error,
}: InputProps) => {
  const { hideDialog } = useGlobalDialogContext();
  const logs = useLogsManager();
  const [isLoading, setLoading] = useState(false);
  const [smsDelivered, setSmsDelivered] = useState(false);
  const [driverSmsStatus, setDriverSmsStatus] = useState<any>();
  const smsMessageStatusSubscription = useWebSocketClientMessageSubscription(
    WebSocketClientMessageTypes.SMS_MESSAGE_STATUS_UPDATED,
    300 * 1000
  );

  const [errorSendingSms, setErrorSendingSms] = useState<
    TwilioError | undefined
  >(error);

  const [sendDriverRequestSms, { error: errorSendingDriverRequestSms }] =
    requestService.endpoints.sendDriverRequestSms.useMutation();

  const [
    sendDriverRequestSmsToNewNumber,
    { error: errorSendingDriverRequestSmsToNewNumber },
  ] = requestService.endpoints.sendDriverRequestSmsToNewNumber.useMutation();

  const handleDialogToggle = () => {
    hideDialog();
  };

  useEffect(() => {
    if (driverSmsStatus?.status === ETwilioCallbackSmsStatus.delivered) {
      setSmsDelivered(true);
    }
    if (
      driverSmsStatus?.status &&
      [
        ETwilioCallbackSmsStatus.undelivered,
        ETwilioCallbackSmsStatus.failed,
      ].includes(driverSmsStatus.status as ETwilioCallbackSmsStatus)
    ) {
      setLoading(false);
      setErrorSendingSms({
        ...twilioUndeliveredSmsError,
        code: driverSmsStatus?.code || 0,
      });
    }
  }, [driverSmsStatus?.status]);

  useEffect(() => {
    if (
      errorSendingDriverRequestSms ||
      errorSendingDriverRequestSmsToNewNumber
    ) {
      setLoading(false);
    }

    setErrorSendingSms(
      // @ts-ignore
      errorSendingDriverRequestSms?.data ||
        // @ts-ignore
        errorSendingDriverRequestSmsToNewNumber?.data
    );
  }, [errorSendingDriverRequestSms, errorSendingDriverRequestSmsToNewNumber]);

  const handleSendMessage = (
    text: string,
    newNumber?: string,
    documentRequestId?: string
  ) => {
    let requestId = request?.id;
    if (!requestId) {
      if (documentRequestId) {
        requestId = documentRequestId;
      } else {
        return;
      }
    }
    setLoading(true);
    if (!newNumber) {
      sendDriverRequestSms({
        documentRequestId: requestId,
        data: {
          text,
        },
      });
    } else {
      sendDriverRequestSmsToNewNumber({
        documentRequestId: requestId,
        data: {
          text,
          number: newNumber,
        },
      });
    }

    smsMessageStatusSubscription.subscribe(
      (message: any) => {
        setDriverSmsStatus(message);
      },
      () => {
        logs.logError('Timeout for SMS_MESSAGE_STATUS_UPDATED WS message', {
          documentRequestId,
        });
        setErrorSendingSms(twilioUndeliveredSmsError);
      }
    );
  };

  return (
    <MessageDeliveryErrorDialog
      isLoading={isLoading}
      isDelivered={smsDelivered}
      open={true}
      setOpen={() => {
        handleDialogToggle();
      }}
      request={request}
      candidateUploadLink={buildLinkToCandidateAuth(request?.candidateCode)}
      onChangePhone={(newPhone) => {
        handleSendMessage(lastUsedText, newPhone);
      }}
      error={errorSendingSms || error}
    />
  );
};
